import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  DIAS_SEMANA,
  repositorioUmbrales,
  type TipoUmbral,
  type UmbralConfigurado,
} from '../../../data/repositories/RepositorioUmbrales';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { repositorioActuadores } from '../../../data/repositories/RepositorioActuadores';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import { FormularioUmbral, type UmbralCompleto } from './FormularioUmbral';
import estilos from './Umbrales.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

/** Resume las franjas horarias en una frase corta para la tabla. */
function resumirHorarios(umbral: UmbralConfigurado): string {
  if (umbral.horarios.length === 0) return 'Siempre';

  const primera = umbral.horarios[0];
  if (!primera) return 'Siempre';

  const dias =
    primera.dias.length === 7
      ? 'todos los días'
      : primera.dias.map((d) => DIAS_SEMANA[d]?.slice(0, 3) ?? '').join(', ');

  const extra = umbral.horarios.length > 1 ? ` +${umbral.horarios.length - 1}` : '';
  return `${primera.horaInicio}–${primera.horaFin} · ${dias}${extra}`;
}

interface UmbralesSensoresProps {
  tipo: TipoUmbral;
}

const TEXTOS: Record<TipoUmbral, { titulo: string; descripcion: string; vacio: string }> = {
  analogico: {
    titulo: 'Umbrales analógicos',
    descripcion: 'Rangos que, al superarse, generan una alarma.',
    vacio: 'Cuando definas un rango para un sensor de medida aparecerá aquí.',
  },
  digital: {
    titulo: 'Umbrales digitales',
    descripcion: 'Estados esperados que, al incumplirse, generan una alarma.',
    vacio: 'Cuando definas el estado esperado de un sensor aparecerá aquí.',
  },
};

/**
 * Configuración de umbrales de sensores.
 *
 * Analógicos y digitales comparten pantalla porque sólo se diferencian en qué
 * define la condición: un rango de valores o un estado esperado. Todo lo demás
 * —gravedad, retardo, retención, avisos— es idéntico.
 */
export function UmbralesSensores({ tipo }: UmbralesSensoresProps) {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_umbrales') ?? false;

  const [busqueda, setBusqueda] = useState('');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<UmbralConfigurado | null>(null);
  const [soloActivos, setSoloActivos] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [formularioAbierto, setFormularioAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<UmbralConfigurado | null>(null);

  // La vista de umbrales sólo expone los nombres de cliente, área y nodo, no
  // sus identificadores, así que la cascada se construye con lo ya cargado.
  const [cliente, setCliente] = useState('');
  const [area, setArea] = useState('');
  const [nodo, setNodo] = useState('');

  const consulta = useQuery({
    queryKey: ['umbrales', tipo],
    queryFn: () => repositorioUmbrales.listar(tipo),
  });

  const sensores = useQuery({
    queryKey: ['sensores', {}],
    queryFn: () => repositorioSensores.listarConValores({}),
  });

  const actuadores = useQuery({
    queryKey: ['actuadores', {}],
    queryFn: () => repositorioActuadores.listar({}),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['umbrales', tipo] });
  };

  const guardar = useMutation({
    mutationFn: async ({ datos, horarios, reglas }: UmbralCompleto) => {
      // El umbral se guarda primero porque sus anexos necesitan su
      // identificador; al crear uno nuevo, ese identificador no existe hasta
      // que la base lo devuelve.
      const id = enEdicion
        ? (await repositorioUmbrales.actualizar(tipo, enEdicion.id, datos), enEdicion.id)
        : await repositorioUmbrales.crear(tipo, datos);

      if (tipo === 'digital') await repositorioUmbrales.guardarHorarios(id, horarios);
      await repositorioUmbrales.guardarReglas(tipo, id, reglas);
    },
    onSuccess: () => {
      toast.success(enEdicion ? 'Umbral actualizado' : 'Umbral creado');
      setFormularioAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('umbrales.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el umbral');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => repositorioUmbrales.eliminar(tipo, id),
    onSuccess: () => {
      toast.success('Umbral eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError('umbrales.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar el umbral');
    },
  });

  const alternarActivo = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      repositorioUmbrales.cambiarActivo(tipo, id, activo),
    onSuccess: refrescar,
    onError: (e) => {
      registrarError('umbrales.cambiarActivo', e);
      toast.error('No se pudo cambiar el estado del umbral');
    },
  });

  const umbrales = useMemo(() => consulta.data ?? [], [consulta.data]);

  /** Opciones de cada nivel de la cascada, derivadas del nivel superior. */
  const clientes = useMemo(
    () => [...new Set(umbrales.map((u) => u.clienteNombre).filter((n): n is string => !!n))].sort(),
    [umbrales],
  );

  const areas = useMemo(
    () =>
      [
        ...new Set(
          umbrales
            .filter((u) => !cliente || u.clienteNombre === cliente)
            .map((u) => u.areaNombre)
            .filter((n): n is string => !!n),
        ),
      ].sort(),
    [umbrales, cliente],
  );

  const nodos = useMemo(
    () =>
      [
        ...new Set(
          umbrales
            .filter(
              (u) => (!cliente || u.clienteNombre === cliente) && (!area || u.areaNombre === area),
            )
            .map((u) => u.nodoNombre)
            .filter((n): n is string => !!n),
        ),
      ].sort(),
    [umbrales, cliente, area],
  );

  const filtrados = useMemo(
    () =>
      umbrales.filter((u) => {
        if (cliente && u.clienteNombre !== cliente) return false;
        if (area && u.areaNombre !== area) return false;
        if (nodo && u.nodoNombre !== nodo) return false;

        if (soloActivos === 'activos' && !u.activo) return false;
        if (soloActivos === 'inactivos' && u.activo) return false;

        const t = busqueda.trim().toLowerCase();
        if (!t) return true;

        return [u.sensorNombre, u.nodoNombre, u.areaNombre, u.clienteNombre, u.mensaje].some(
          (campo) => campo?.toLowerCase().includes(t),
        );
      }),
    [umbrales, busqueda, soloActivos, cliente, area, nodo],
  );

  const chips: ChipFiltro[] = [];
  if (cliente) {
    chips.push({
      clave: 'cliente',
      etiqueta: `Cliente: ${cliente}`,
      quitar: () => {
        setCliente('');
        setArea('');
        setNodo('');
      },
    });
  }
  if (area) {
    chips.push({
      clave: 'area',
      etiqueta: `Área: ${area}`,
      quitar: () => {
        setArea('');
        setNodo('');
      },
    });
  }
  if (nodo) {
    chips.push({ clave: 'nodo', etiqueta: `Nodo: ${nodo}`, quitar: () => setNodo('') });
  }
  if (soloActivos !== 'todos') {
    chips.push({
      clave: 'estado',
      etiqueta: soloActivos === 'activos' ? 'Solo activos' : 'Solo inactivos',
      quitar: () => setSoloActivos('todos'),
    });
  }
  if (busqueda.trim()) {
    chips.push({
      clave: 'busqueda',
      etiqueta: `Búsqueda: «${busqueda.trim()}»`,
      quitar: () => setBusqueda(''),
    });
  }

  const columnas: Columna<UmbralConfigurado>[] = [
    {
      clave: 'sensor',
      encabezado: 'Sensor',
      movil: 'titulo',
      render: (u) => <span className={estilos.sensor}>{u.sensorNombre}</span>,
    },
    {
      clave: 'ubicacion',
      encabezado: 'Ubicación',
      movil: 'subtitulo',
      render: (u) => (
        <span className={estilos.ubicacion}>
          {[u.clienteNombre, u.areaNombre, u.nodoNombre].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      clave: 'condicion',
      encabezado: tipo === 'analogico' ? 'Rango permitido' : 'Estado esperado',
      render: (u) => {
        if (tipo === 'digital') {
          if (u.estadoEsperado === null) return '—';
          return <Badge color="var(--status-info)">{u.estadoEsperado ? 'ON' : 'OFF'}</Badge>;
        }

        const { valorMinimo: min, valorMaximo: max } = u;
        const texto =
          min !== null && max !== null
            ? `${min} – ${max}`
            : min !== null
              ? `≥ ${min}`
              : max !== null
                ? `≤ ${max}`
                : '—';

        return <span className={estilos.rango}>{texto}</span>;
      },
    },
    {
      clave: 'criticidad',
      encabezado: 'Gravedad',
      movil: 'detalle',
      render: (u) => <Badge color={u.criticidad.color}>{u.criticidad.etiqueta}</Badge>,
    },
    {
      clave: 'retardo',
      encabezado: 'Retardo',
      render: (u) => (u.retardoSegundos ? `${u.retardoSegundos} s` : '—'),
    },
    ...(tipo === 'digital'
      ? [
          {
            clave: 'horario',
            encabezado: 'Vigila',
            render: (u: UmbralConfigurado) => (
              <span className={estilos.ubicacion}>{resumirHorarios(u)}</span>
            ),
          } satisfies Columna<UmbralConfigurado>,
        ]
      : []),
    {
      clave: 'acciones_auto',
      encabezado: 'Acciones',
      render: (u) =>
        u.reglas.length === 0 ? (
          <Badge color="var(--text-3)">Solo avisa</Badge>
        ) : (
          <span className={estilos.anexos} title={u.reglas.map((r) => r.actuadorNombre).join(', ')}>
            <Badge color="var(--status-info)">
              {u.reglas.length === 1 ? u.reglas[0]?.actuadorNombre : `${u.reglas.length} equipos`}
            </Badge>
          </span>
        ),
    },
    {
      clave: 'avisos',
      encabezado: 'Avisos',
      render: (u) =>
        u.notificar ? (
          <Badge color="var(--status-success)">Sí</Badge>
        ) : (
          <Badge color="var(--text-3)">No</Badge>
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      movil: 'detalle',
      render: (u) =>
        u.activo ? (
          <Badge color="var(--status-success)">Activo</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactivo</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (u) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={u.activo ? 'interruptorOn' : 'interruptorOff'}
            title={u.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => alternarActivo.mutate({ id: u.id, activo: !u.activo })}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(u)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => {
              setEnEdicion(u);
              setFormularioAbierto(true);
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={
              puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
            }
            onClick={() => {
              const seguro = window.confirm(
                `¿Eliminar el umbral de «${u.sensorNombre}»?\n\n` +
                  'Ese sensor dejará de generar alarmas hasta que se le configure otro.',
              );
              if (seguro) eliminar.mutate(u.id);
            }}
          />
        </div>
      ),
    },
  ];

  const textos = TEXTOS[tipo];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo={textos.titulo}
        descripcion={textos.descripcion}
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={consulta.isFetching}
            >
              Actualizar
            </Button>
            <Button
              icono="agregar"
              compactoEnMovil
              motivoDeshabilitado={
                puedeGestionar ? null : 'Tu rol no puede modificar la configuración'
              }
              onClick={() => {
                setEnEdicion(null);
                setFormularioAbierto(true);
              }}
            >
              Nuevo umbral
            </Button>
          </>
        }
      />

      <BarraFiltros
        chips={chips}
        acciones={
          chips.length > 0 && (
            <Button
              variante="fantasma"
              icono="cerrar"
              onClick={() => {
                setCliente('');
                setArea('');
                setNodo('');
                setSoloActivos('todos');
                setBusqueda('');
              }}
            >
              Limpiar filtros
            </Button>
          )
        }
      >
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={cliente}
          onChange={(e) => {
            setCliente(e.target.value);
            setArea('');
            setNodo('');
          }}
          opciones={clientes.map((c) => ({ valor: c, etiqueta: c }))}
        />
        <Select
          etiqueta="Área"
          placeholder={cliente ? 'Todas las áreas' : 'Todas'}
          value={area}
          onChange={(e) => {
            setArea(e.target.value);
            setNodo('');
          }}
          opciones={areas.map((a) => ({ valor: a, etiqueta: a }))}
        />
        <Select
          etiqueta="Nodo"
          placeholder={area ? 'Todos los nodos' : 'Todos'}
          value={nodo}
          onChange={(e) => setNodo(e.target.value)}
          opciones={nodos.map((n) => ({ valor: n, etiqueta: n }))}
        />
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Sensor o mensaje"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Estado"
          value={soloActivos}
          onChange={(e) => setSoloActivos(e.target.value as typeof soloActivos)}
          opciones={[
            { valor: 'todos', etiqueta: 'Todos' },
            { valor: 'activos', etiqueta: 'Solo activos' },
            { valor: 'inactivos', etiqueta: 'Solo inactivos' },
          ]}
        />
      </BarraFiltros>

      {consulta.isLoading ? (
        <ListaEsqueleto filas={6} />
      ) : consulta.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="umbrales"
          titulo={umbrales.length === 0 ? 'Sin umbrales configurados' : 'Ninguno coincide'}
          descripcion={
            umbrales.length === 0 ? textos.vacio : 'Prueba a cambiar la búsqueda o el filtro.'
          }
        />
      ) : (
        <>
          <Tabla
            datos={filtrados}
            columnas={columnas}
            claveFila={(u) => u.id}
            etiqueta={textos.titulo}
          />

          <FichaRegistro
            item={enFicha}
            titulo="Umbral"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <FormularioUmbral
        abierto={formularioAbierto}
        tipo={tipo}
        umbral={enEdicion}
        sensores={sensores.data ?? []}
        actuadores={actuadores.data ?? []}
        guardando={guardar.isPending}
        onCerrar={() => {
          setFormularioAbierto(false);
          setEnEdicion(null);
        }}
        onGuardar={(datos) => guardar.mutate(datos)}
      />
    </div>
  );
}

export function UmbralesAnalogicos() {
  return <UmbralesSensores tipo="analogico" />;
}

export function UmbralesDigitales() {
  return <UmbralesSensores tipo="digital" />;
}
